import { cn } from "@/lib/utils";
import type { WinnersFeedItemDto } from "@/app/redux/features/api/winnersFeed.types";
import ClockIcon from "@/app/shared-components/icons/ClockIcon";
import LocationPinIcon from "@/app/shared-components/icons/LocationPinIcon";
import ArrowRightIcon from "@/app/shared-components/icons/ArrowRightIcon";
import {
  formatCurrency,
  formatTicketPrice,
  getAccentColor,
  getFallbackImage,
  PASS_LABEL,
} from "@/app/(general-header)/home/winnersFeed/winnersFeedUtils";
import { useCompetition, useCompetitionsCategoriesLoading } from "@/app/redux/features/categories/selectors";

function FeedCardVerticalImage({ item }: { item: WinnersFeedItemDto }) {
  const competitionsLoading = useCompetitionsCategoriesLoading();
  const competition = useCompetition({ CompetitionId: item.CompetitionId });

  return (
    <div className="relative h-[76px] lg:h-[105px] w-full">
      <div
        className={cn(
          "absolute inset-x-0 top-0 h-[76px] lg:h-[105px] overflow-hidden rounded-tl-[12px] lg:rounded-tl-2xl rounded-tr-[12px] lg:rounded-tr-2xl isolate transition-opacity",
          !competitionsLoading ? "opacity-100" : "opacity-0"
        )}>
        {item.WinnerThumbnailUrl ? (
          <img
            alt="WinnerThumbnailUrl"
            className="absolute inset-0 object-cover object-top size-full z-[1]"
            draggable={false}
            src={item.WinnerThumbnailUrl}
          />
        ) : competition?.ImageUrl ? (
          <>
            <img
              alt="CompetitionImageUrl"
              className="absolute inset-0 object-cover object-top size-full z-[1]"
              draggable={false}
              src={competition.ImageUrl}
            />
            <div className="absolute inset-0 backdrop-blur-[3px] bg-white/30 rounded z-[2]" />
          </>
        ) : getFallbackImage(item.CompetitionTypePrefix) ? (
          <>
            <img
              alt=""
              className="absolute inset-0 object-cover object-top size-full z-0"
              draggable={false}
              src={getFallbackImage(item.CompetitionTypePrefix)!}
            />
            <div className="absolute inset-0 backdrop-blur-[3px] bg-white/30 rounded z-[2]" />
            <div className="absolute inset-0 bg-gradient-to-b from-transparent from-[50%] via-white/79 via-[85%] to-white z-[3]" />
          </>
        ) : (
          <div className="absolute inset-0 z-0" style={{ backgroundColor: getAccentColor(item.NavigationColour) }} />
        )}
        {(item.WinnerThumbnailUrl || competition?.ImageUrl) && (
          <div className="absolute inset-0 bg-gradient-to-b from-transparent from-[50%] via-white/79 via-[85%] to-white z-[3]" />
        )}
        {!item.WinnerThumbnailUrl && item.ThumbnailUrl && (
          <img
            alt={item.PrizeName}
            className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[112px] lg:w-[155px] h-[68px] lg:h-[94px] object-contain z-[4] will-change-transform"
            draggable={false}
            src={item.ThumbnailUrl}
          />
        )}
      </div>
    </div>
  );
}

export default function FeedCardVertical({ item }: { item: WinnersFeedItemDto }) {
  return (
    <div
      className="flex flex-col bg-white border border-black/8 overflow-hidden relative rounded-[12px] lg:rounded-2xl w-full h-[190px] lg:h-[262px]"
      style={{ "--accent": getAccentColor(item.NavigationColour) } as React.CSSProperties}>
      <FeedCardVerticalImage item={item} />

      {/* Info */}
      <div className="flex flex-col flex-1 justify-between px-[7px] lg:px-3 pt-1.5 pb-3 lg:pb-[13px] lg:pt-2">
        <div className="flex flex-col gap-2 lg:gap-3">
          {/* Timestamp */}
          <div className="flex gap-[3px] lg:gap-1 items-center">
            <ClockIcon className="w-[10px] h-[10px] lg:w-3 lg:h-3 shrink-0 text-gray-1" />
            <span className="font-jost font-medium text-[9px] lg:text-xs text-gray-1 leading-[10px] lg:leading-4">
              {item.RevealedText}
            </span>
          </div>

          {/* Winner name + prize */}
          <div className="flex flex-col gap-0.5 lg:gap-1">
            <div className="flex items-center gap-2 lg:gap-2">
              <p className="font-jost font-bold text-base lg:text-[22px] leading-5 lg:leading-7 whitespace-nowrap [color:var(--accent)]">
                {item.WinnerDisplayName}
              </p>
              {item.Location && (
                <div className="flex items-center gap-[2px] lg:gap-[3px] shrink-0">
                  <LocationPinIcon className="w-[10px] h-[10px] lg:w-3 lg:h-3 text-gray-5" />
                  <span className="font-jost font-medium text-[9px] lg:text-xs leading-[10px] lg:leading-4 text-gray-5 whitespace-nowrap">
                    {item.Location}
                  </span>
                </div>
              )}
            </div>
            <p className="font-jost font-semibold text-xs lg:text-base leading-4 lg:leading-[22px] text-secondary truncate">
              {item.PrizeName}
            </p>
          </div>
        </div>

        {/* Price + Won for / Won with */}
        <div className="flex items-end justify-between">
          {item.IsBotbPass && item.PassType ? (
            <>
              {item.TicketPriceGBP > 0 && item.PrizeValueGBP >= 2000 && !item.IsCashPrize && (
                <div className="flex items-center justify-center h-[18px] px-[7px] py-[3px] lg:h-auto lg:px-2.5 lg:py-1.5 rounded-full border border-black/8 [background-color:var(--accent)]">
                  <span className="font-jost font-semibold italic text-[11px] lg:text-sm text-white leading-[14px] lg:leading-3 whitespace-nowrap">
                    {formatCurrency(item.PrizeValueGBP)}
                  </span>
                </div>
              )}
              <div className="flex justify-center bg-[#C5EBC7] border border-[#A9E2AC] rounded gap-0.5 px-2 py-[3px] ml-auto max-w-[94px] lg:max-w-[116px] min-h-[36px] lg:min-h-12 flex-col items-start">
                <p className="font-jost font-medium text-[11px] lg:text-sm text-green-5 whitespace-nowrap leading-[14px] lg:leading-[18px]">
                  Won with
                </p>
                <a
                  href="/botb-pass?utm_medium=winner_widget"
                  className="font-jost font-medium text-[11px] lg:text-sm text-[#3268E8] underline whitespace-nowrap leading-3 flex items-center">
                  {PASS_LABEL[item.PassType]}
                  <ArrowRightIcon className="w-3 h-3 text-[#3268E8] relative top-px" />
                </a>
              </div>
            </>
          ) : (
            <>
              {item.PrizeValueGBP >= 2000 && !item.IsCashPrize && (
                <div className="flex items-center justify-center min-h-[18px] px-[7px] py-px lg:min-h-6 lg:px-2.5 lg:py-1 rounded-full border border-black/8 [background-color:var(--accent)]">
                  <span className="font-jost font-semibold italic text-[11px] lg:text-sm text-white leading-[14px] lg:leading-3 whitespace-nowrap">
                    {formatCurrency(item.PrizeValueGBP)}
                  </span>
                </div>
              )}
              <div className="flex items-center justify-center min-h-[18px] lg:min-h-6 bg-[#C5EBC7] border border-[#A9E2AC] rounded px-2 py-px lg:py-0.5">
                <p className="font-jost font-medium text-[11px] lg:text-sm text-green-5 whitespace-nowrap leading-[14px] lg:leading-[18px]">
                  Won for <span className="font-bold">{formatTicketPrice(item.TicketPriceGBP)}</span>
                </p>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Bottom accent bar */}
      <div className="absolute bottom-0 left-0 h-1 lg:h-[5px] w-full [background-color:var(--accent)]" />
    </div>
  );
}
